import { useState } from "react";
import Input from "components/ui/Input";
import {
  useTestRemoteConnection,
  useMigrateCompanyData,
} from "http-hooks/company/useCompanyMigration";
import {
  ServerIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

const initialForm = {
  remoteHost: "",
  remotePort: "3306",
  remoteUser: "",
  remotePassword: "",
  remoteDatabase: "",
};

export default function MigrateCompany({ companyId, companyName, onClose }) {
  const [form, setForm] = useState(initialForm);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);

  const { mutateAsync: testConnection, isPending: isTesting } =
    useTestRemoteConnection();
  const { mutateAsync: migrateData, isPending: isMigrating } =
    useMigrateCompanyData();

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setConnectionInfo(null);
    setMigrationResult(null);
  };

  const handleTestConnection = async () => {
    try {
      const result = await testConnection(form);
      setConnectionInfo(result);
    } catch {
      setConnectionInfo(null);
    }
  };

  const handleMigrate = async () => {
    try {
      const result = await migrateData({ ...form, companyId });
      setMigrationResult(result);
    } catch {
      setMigrationResult(null);
    }
  };

  const isFormValid =
    form.remoteHost && form.remoteUser && form.remotePassword && form.remoteDatabase;

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
          <ServerIcon className="h-6 w-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Migrate Company Data
          </h3>
          <p className="text-sm text-gray-500">
            {companyName} (ID: {companyId})
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
        <div className="flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">This will overwrite all data in the target company database.</p>
            <p className="mt-1">
              Connects to the remote (old) database, copies all table data into company{" "}
              <strong>{companyId}</strong>, and runs post-migration steps
              (profiling, discounts, cndntypes).
            </p>
          </div>
        </div>
      </div>

      {/* Connection Form */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700">
          Remote Database Connection
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Host *"
            type="text"
            value={form.remoteHost}
            onChange={handleChange("remoteHost")}
            placeholder="e.g. 192.168.1.100"
          />
          <Input
            label="Port"
            type="text"
            value={form.remotePort}
            onChange={handleChange("remotePort")}
            placeholder="3306"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Username *"
            type="text"
            value={form.remoteUser}
            onChange={handleChange("remoteUser")}
            placeholder="Database username"
          />
          <Input
            label="Password *"
            type="password"
            value={form.remotePassword}
            onChange={handleChange("remotePassword")}
            placeholder="Database password"
          />
        </div>
        <Input
          label="Database Name *"
          type="text"
          value={form.remoteDatabase}
          onChange={handleChange("remoteDatabase")}
          placeholder="e.g. agentio_10000002"
        />
      </div>

      {/* Test Connection Result */}
      {connectionInfo && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">
              Connected — {connectionInfo.tableCount} tables found
            </span>
          </div>
          <div className="max-h-40 overflow-y-auto">
            <table className="text-xs w-full">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="pb-1">Table</th>
                  <th className="pb-1 text-right">~Rows</th>
                </tr>
              </thead>
              <tbody>
                {connectionInfo.tables?.map((t) => (
                  <tr key={t.name} className="border-t border-green-100">
                    <td className="py-0.5 text-gray-700">{t.name}</td>
                    <td className="py-0.5 text-right text-gray-500">
                      {t.approxRows}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Migration Result */}
      {migrationResult && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              Migration Complete — {migrationResult.tablesProcessed} tables
              processed
            </span>
          </div>

          {/* Tables truncated */}
          {migrationResult.tablesTruncated?.length > 0 && (
            <p className="text-xs text-gray-600 mb-1">
              Seed tables truncated:{" "}
              {migrationResult.tablesTruncated.join(", ")}
            </p>
          )}

          {/* Row counts */}
          <div className="max-h-40 overflow-y-auto mt-2">
            <table className="text-xs w-full">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="pb-1">Table</th>
                  <th className="pb-1 text-right">Rows Copied</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(migrationResult.rowsCopied || {}).map(
                  ([table, count]) => (
                    <tr key={table} className="border-t border-blue-100">
                      <td className="py-0.5 text-gray-700">{table}</td>
                      <td className="py-0.5 text-right text-gray-500">
                        {count}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Post-migration */}
          {migrationResult.postMigration && (
            <div className="mt-2 border-t border-blue-200 pt-2">
              <p className="text-xs font-medium text-gray-700 mb-1">
                Post-Migration Steps:
              </p>
              {Object.entries(migrationResult.postMigration).map(
                ([step, result]) => (
                  <div key={step} className="flex items-center gap-1.5 text-xs">
                    {result.success ? (
                      <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <XCircleIcon className="h-3.5 w-3.5 text-red-500" />
                    )}
                    <span className="text-gray-700">{step}</span>
                    {result.success && (
                      <span className="text-gray-400">
                        {result.rowsInserted != null &&
                          `(${result.rowsInserted} inserted)`}
                        {result.rowsUpdated != null &&
                          `(${result.rowsUpdated} updated)`}
                        {result.processedSalebills != null &&
                          `(${result.processedSalebills} bills, ${result.discountRecordsCreated} records)`}
                        {result.processed != null &&
                          result.success != null &&
                          result.processedSalebills == null &&
                          `(${result.success} of ${result.processed})`}
                      </span>
                    )}
                    {!result.success && (
                      <span className="text-red-500">{result.error}</span>
                    )}
                  </div>
                )
              )}
            </div>
          )}

          {/* Errors */}
          {migrationResult.errors?.length > 0 && (
            <div className="mt-2 border-t border-red-200 pt-2">
              <p className="text-xs font-medium text-red-700 mb-1">
                Errors ({migrationResult.errors.length}):
              </p>
              {migrationResult.errors.slice(0, 5).map((err, i) => (
                <p key={i} className="text-xs text-red-600">
                  {err.table}: {err.error}
                </p>
              ))}
            </div>
          )}

          {/* Skipped tables */}
          {migrationResult.tablesSkipped?.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-gray-500 cursor-pointer">
                {migrationResult.tablesSkipped.length} tables skipped
              </summary>
              {migrationResult.tablesSkipped.map((s, i) => (
                <p key={i} className="text-xs text-gray-400 ml-4">
                  {s.table}: {s.reason}
                </p>
              ))}
            </details>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-white px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Close
        </button>
        <button
          type="button"
          onClick={handleTestConnection}
          disabled={!isFormValid || isTesting}
          className="inline-flex items-center gap-1.5 rounded-md bg-gray-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isTesting && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
          Test Connection
        </button>
        <button
          type="button"
          onClick={handleMigrate}
          disabled={!isFormValid || !connectionInfo || isMigrating}
          className="inline-flex items-center gap-1.5 rounded-md bg-purple-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isMigrating && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
          {isMigrating ? "Migrating..." : "Start Migration"}
        </button>
      </div>
    </div>
  );
}
